import { Bundle, Resource } from "@medplum/fhirtypes";
import { isAxiosError } from "axios";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { EhrDetails, EhrName, EhrWritePath } from "../../../../domain/ehr";
import { usePatientConsolidatedContext } from "../consolidated-context";
import {
  MappedConsolidatedResources,
  PatientConsolidatedStateActionType,
} from "../consolidated-context/reducer";
import { useGetEhrDiffRQ } from "../useGetEhrDiffRQ";
import useMetriportToast from "../useMetriportToast";

type WriteBackStatus = {
  writingIds: string[];
  successIds: string[];
  failedIds: { id: string; error: unknown }[];
};

const statusInitState: WriteBackStatus = {
  writingIds: [],
  successIds: [],
  failedIds: [],
};

export type CurrentEhr = {
  isAthena: boolean;
  isEClinicalWorks: boolean;
  isCanvas: boolean;
  isElation: boolean;
  isHealthie: boolean;
  isSalesforce: boolean;
};

export type EhrContextValue = {
  isEhr: boolean;
  ehrDetails?: EhrDetails;
  isFetchingDiff: boolean;
  writeBackToEhr: (params: {
    resourceId: string;
    resource: object;
    path: EhrWritePath;
  }) => Promise<void>;
  writeBackStatus: WriteBackStatus;
  isShowingDiff: boolean;
  toggleShowDiff?: () => void;
  consolidatedDiff?: Bundle<Resource>;
  currentEhr: CurrentEhr;
};

const EhrContext = createContext<EhrContextValue | undefined>(undefined);

interface EhrProviderProps {
  patientId: string;
  children: ReactNode;
  // When cleaning up V1 code, remove ehr props 'ehrDetails' from 'sharedState'
  ehrDetails?: EhrDetails;
}

export function EhrProvider({ patientId, children, ehrDetails }: EhrProviderProps) {
  const [writeBackStatus, dispatchWriteBackStatus] = useReducer(statusReducer, statusInitState);
  const [isShowingDiff, setIsShowingDiff] = useState(false);
  const [resourceDiff, setResourceDiff] = useState<MappedConsolidatedResources>();
  const [consolidatedDiff, setConsolidatedDiff] = useState<Bundle<Resource>>();

  const toast = useMetriportToast();
  const { name: ehrName, ehrPatientId, diff, write: writeBack } = ehrDetails ?? {};
  const { patientConsolidatedDispatch, patientConsolidatedState } = usePatientConsolidatedContext();

  const consolidatedResources = patientConsolidatedState[patientId]?.consolidated;

  const currentEhr = useMemo(() => {
    return {
      isAthena: ehrName === EhrName.Athena,
      isEClinicalWorks: ehrName === EhrName.EClinicalWorks,
      isCanvas: ehrName === EhrName.Canvas,
      isElation: ehrName === EhrName.Elation,
      isHealthie: ehrName === EhrName.Healthie,
      isSalesforce: ehrName === EhrName.Salesforce,
    };
  }, [ehrName]);

  function toggleShowDiff() {
    setIsShowingDiff(prev => !prev);
  }

  const displayResources = isShowingDiff ? resourceDiff : consolidatedResources;
  const displayConsolidated = isShowingDiff ? consolidatedDiff : undefined;
  const displayToggleShowDiff = consolidatedDiff ? toggleShowDiff : undefined;

  const {
    isFetchingDiff,
    metriportOnlyBundle,
    metriportOnlyBundleResources,
    metriportOnlyResourceTypes,
    getMetriportOnlyBundleJob,
  } = useGetEhrDiffRQ({ ehr: ehrName, ehrPatientId, diff });

  /**
   * kick off the diff job once consolidated is loaded
   */
  const hasRunDiffJob = useRef(false);
  useEffect(() => {
    if (consolidatedResources && ehrPatientId && !hasRunDiffJob.current) {
      hasRunDiffJob.current = true;
      getMetriportOnlyBundleJob();
    }
  }, [consolidatedResources, ehrPatientId, getMetriportOnlyBundleJob]);

  const hasMetriportOnlyResources =
    metriportOnlyBundleResources && Object.keys(metriportOnlyBundleResources).length > 0;
  const hasAllResources = consolidatedResources && Object.keys(consolidatedResources).length > 0;
  const canShowResourceDiff =
    hasMetriportOnlyResources && hasAllResources && metriportOnlyResourceTypes;

  /**
   * Compute which resources are present in Metriport but *not* in EHR.
   * Run once after useGetConsolidatedRQ() and getMetriportOnlyBundleJob()
   * Computes:
   *   - resourses (backwards compatibilty, for consolidated context)
   *   - new bundle via FhirBundleSdk
   */
  const hasComputedDiff = useRef(false);
  useEffect(() => {
    if (!canShowResourceDiff || hasComputedDiff.current) return;
    hasComputedDiff.current = true;

    const resourceDiff: MappedConsolidatedResources = {};
    const metriportOnlyResourceIds = new Set<string>();

    for (const resourceMap of Object.values(metriportOnlyBundleResources)) {
      for (const resourceId of Object.keys(resourceMap)) metriportOnlyResourceIds.add(resourceId);
    }

    Object.entries(consolidatedResources).forEach(([resourceType, resourceMap]) => {
      if (!metriportOnlyResourceTypes.includes(resourceType)) {
        resourceDiff[resourceType] = resourceMap;
        return;
      }
      Object.entries(resourceMap).forEach(([resourceId, resource]) => {
        if (!metriportOnlyResourceIds.has(resourceId)) return;
        resourceDiff[resourceType] = {
          ...(resourceDiff[resourceType] ?? {}),
          [resourceId]: resource,
        };
      });
    });

    // backwards compatibilty
    setResourceDiff(resourceDiff);

    // set bundle for V2 FhirSDK use
    setConsolidatedDiff({
      ...(metriportOnlyBundle ?? {}),
      resourceType: "Bundle",
      entry: Object.values(resourceDiff)
        .flatMap(resourceMap => Object.values(resourceMap))
        .map(resource => ({ resource })),
    });
  }, [
    canShowResourceDiff,
    consolidatedResources,
    metriportOnlyBundle,
    metriportOnlyResourceTypes,
    metriportOnlyBundleResources,
  ]);

  /**
   * Backwards compatible, swap resources in consolidated context.
   * (this could be depricated in v2 as we work with FhirSdk)
   */
  useEffect(() => {
    if (!displayResources) return;
    patientConsolidatedDispatch({
      type: PatientConsolidatedStateActionType.setPatientsConsolidated,
      patientId,
      consolidatedResources: displayResources,
    });
  }, [isShowingDiff, displayResources, patientId]);

  async function writeBackToEhr({
    resourceId,
    resource,
    path,
  }: {
    resourceId: string;
    resource: object;
    path: EhrWritePath;
  }) {
    if (!writeBack || !ehrPatientId) return;
    try {
      dispatchWriteBackStatus({ type: "START_WRITE", resourceId });
      await writeBack({ patientId: ehrPatientId, resource, path });
      dispatchWriteBackStatus({ type: "WRITE_SUCCESS", resourceId });
      toast.success({ title: `Written to ${ehrName}` });
    } catch (error) {
      let errorMessage = "Unknown error";
      if (isAxiosError(error)) {
        const message = error.response?.data?.detail ?? error.message;
        if (typeof message === "string") errorMessage = message;
      }
      dispatchWriteBackStatus({ type: "WRITE_FAILURE", resourceId, error: errorMessage });
      toast.error({ title: "Write failed", description: errorMessage });
    }
  }

  return (
    <EhrContext.Provider
      value={{
        isEhr: !!ehrDetails,
        ehrDetails,
        writeBackToEhr,
        writeBackStatus,
        isShowingDiff,
        isFetchingDiff,
        toggleShowDiff: displayToggleShowDiff,
        consolidatedDiff: displayConsolidated,
        currentEhr,
      }}
    >
      {children}
    </EhrContext.Provider>
  );
}

export function useEhrContext(): EhrContextValue {
  const context = useContext(EhrContext);
  if (!context) {
    throw new Error("useEhrContext must be used within EhrProvider");
  }
  return context;
}

type WriteBackAction =
  | { type: "START_WRITE"; resourceId: string }
  | { type: "WRITE_SUCCESS"; resourceId: string }
  | { type: "WRITE_FAILURE"; resourceId: string; error: string };

function statusReducer(state: WriteBackStatus, action: WriteBackAction): WriteBackStatus {
  switch (action.type) {
    case "START_WRITE": {
      return { ...state, writingIds: [...state.writingIds, action.resourceId] };
    }
    case "WRITE_SUCCESS": {
      const writingIds = state.writingIds.filter(id => id !== action.resourceId);
      const successIds = [...state.successIds, action.resourceId];
      return { ...state, writingIds, successIds };
    }
    case "WRITE_FAILURE": {
      const writingIds = state.writingIds.filter(id => id !== action.resourceId);
      const failedIds = [...state.failedIds, { id: action.resourceId, error: action.error }];
      return { ...state, writingIds, failedIds };
    }
    default:
      return state;
  }
}
